import { useMutation, useQuery } from '@apollo/client/react';
import { type ChatCrmContext, chatCrmLockedBy, chatCrmModeOn } from '@src/components/sco/Chat/chatCrmDraft';
import { CLAIM_CRM_THREAD } from '@src/gql/mutations/ClaimCrmThread';
import { DISCARD_TOOL_DRAFT } from '@src/gql/mutations/DiscardToolDraft';
import { RELEASE_CRM_THREAD } from '@src/gql/mutations/ReleaseCrmThread';
import { SEND_TOOL_MESSAGE } from '@src/gql/mutations/SendToolMessage';
import { UPSERT_TOOL_DRAFT } from '@src/gql/mutations/UpsertToolDraft';
import { CHAT_CRM_ACCOUNT, CRM_CHAT_DRAFT } from '@src/gql/queries/CrmChatDraft';
import { prettyErrorMessage } from '@src/lib/mutationErrors';
import { useEffect, useRef } from 'react';

/** How often an open CRM conversation reads the draft again while the tab is visible (D6). */
export const CRM_CHAT_DRAFT_POLL_MS = 8000;

/**
 * Editing renews the claim at most this often. Saves do not claim on the server (assertReplyOwner only
 * checks), so without renewals a claim taken once would lapse after 15 minutes mid-reply.
 */
export const CRM_CHAT_CLAIM_RENEW_MS = 30000;

/** A save or send. `error` is set when `ok` is false (this app has no strictNullChecks to narrow a union). */
export type CrmChatWrite = {
  ok: boolean;
  commentId?: string | null;
  unchanged?: boolean;
  error?: string;
};

/**
 * The on-site composer's side of the shared CRM reply draft (crm-chat-draft-sync D4–D7). Only a crm
 * account asks; for anyone else this reads nothing and `on` stays false. While on, the draft is read
 * again every 8 seconds with the tab visible, and on focus. Writes go through the desk's own mutations
 * (upsertToolDraft, sendToolMessage), so the run, the edit tracking and the audit stay right.
 *
 * The claim (L3): editing claims the reply and keeps renewing it (at most every 30 seconds), and leaving
 * the conversation lets it go. With no edits it lapses on the server after 15 minutes.
 */
export function useCrmChatDraft(input: {
  enabled: boolean;
  parentCommentId?: string | null;
  threadId?: string | null;
  userId?: string | null;
}) {
  const accountQuery = useQuery<{
    currentUser?: {
      crmEnabled?: boolean | null;
    } | null;
  }>(CHAT_CRM_ACCOUNT, {
    skip: !input.enabled || !input.userId
  });
  const isCrmAccount = accountQuery.data?.currentUser?.crmEnabled === true;
  const threadId = input.threadId || null;
  const draftQuery = useQuery<{
    crmChatDraft?: ChatCrmContext | null;
  }>(CRM_CHAT_DRAFT, {
    fetchPolicy: 'network-only',
    skip: !input.enabled || !isCrmAccount || !threadId || Boolean(input.parentCommentId),
    variables: {
      threadId: threadId || ''
    }
  });
  const context = draftQuery.data?.crmChatDraft ?? null;
  const on = chatCrmModeOn({
    context,
    parentCommentId: input.parentCommentId,
    userId: input.userId
  });
  const lockedBy = on ? chatCrmLockedBy(context, input.userId) : null;
  const [upsertDraft] = useMutation(UPSERT_TOOL_DRAFT);
  const [sendMessage] = useMutation(SEND_TOOL_MESSAGE);
  const [discardDraft] = useMutation(DISCARD_TOOL_DRAFT);
  const [claimThread] = useMutation(CLAIM_CRM_THREAD);
  const [releaseThread] = useMutation(RELEASE_CRM_THREAD);
  const claimedFor = useRef<string | null>(null);
  const claimedAt = useRef(0);
  const refetch = draftQuery.refetch;
  useEffect(() => {
    if (!on) {
      return undefined;
    }
    const reread = () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        void refetch();
      }
    };
    const timer = setInterval(reread, CRM_CHAT_DRAFT_POLL_MS);
    window.addEventListener('focus', reread);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', reread);
    };
  }, [on, refetch, threadId]);

  // Leaving the conversation (or the page) lets the claim go, so the desk is not locked out.
  useEffect(() => {
    const release = () => {
      const claimed = claimedFor.current;
      if (claimed) {
        claimedFor.current = null;
        void releaseThread({
          variables: {
            input: {
              threadId: claimed
            }
          }
        }).catch(() => undefined);
      }
    };
    window.addEventListener('pagehide', release);
    return () => {
      window.removeEventListener('pagehide', release);
      release();
    };
  }, [releaseThread, threadId]);
  function ids() {
    return {
      inboundCommentId: context?.inboundCommentId || null,
      seenInboundCommentId: context?.seenInboundCommentId || null,
      threadId: context?.threadId || threadId,
      toolId: context?.toolId || null
    };
  }

  /** Claim the reply on an edit, and renew it while editing goes on. */
  function claim() {
    if (!on || !threadId) {
      return;
    }
    const now = Date.now();
    if (claimedFor.current === threadId && now - claimedAt.current < CRM_CHAT_CLAIM_RENEW_MS) {
      return;
    }
    claimedFor.current = threadId;
    claimedAt.current = now;
    void claimThread({
      variables: {
        input: {
          accountUserId: input.userId,
          threadId
        }
      }
    }).catch(() => {
      claimedFor.current = null;
      claimedAt.current = 0;
    });
  }

  /** Save the box as the CRM draft (Save in the drafts menu). An empty text would discard it. */
  async function save(body: string, storedFileIds: string[]): Promise<CrmChatWrite> {
    try {
      const result = await upsertDraft({
        variables: {
          input: {
            ...ids(),
            body,
            storedFileIds
          }
        }
      });
      const payload = (result.data as {
        upsertToolDraft?: {
          comment?: {
            commentId?: string | null;
          } | null;
          unchanged?: boolean;
        };
      })?.upsertToolDraft;
      return {
        commentId: payload?.comment?.commentId || null,
        ok: true,
        unchanged: Boolean(payload?.unchanged)
      };
    } catch (error) {
      return {
        error: prettyErrorMessage(error),
        ok: false
      };
    }
  }

  /** Send the draft (or the box text) as the account. */
  async function send(body_0: string, storedFileIds_0: string[] | null, commentId: string | null): Promise<CrmChatWrite> {
    try {
      // Null files leave the draft's files as they are (Send from the menu).
      const files = storedFileIds_0 ? {
        storedFileIds: storedFileIds_0
      } : {};
      const result_0 = await sendMessage({
        variables: {
          input: {
            ...ids(),
            body: body_0,
            ...files,
            ...(commentId ? {
              commentId
            } : {})
          }
        }
      });
      const sent = (result_0.data as {
        sendToolMessage?: {
          comment?: {
            commentId?: string | null;
          } | null;
        };
      })?.sendToolMessage;
      return {
        commentId: sent?.comment?.commentId || null,
        ok: true
      };
    } catch (error_0) {
      return {
        error: prettyErrorMessage(error_0),
        ok: false
      };
    }
  }

  /** Delete the draft (the desk's discard), with its files. */
  async function discard(): Promise<CrmChatWrite> {
    try {
      const {
        inboundCommentId,
        threadId: forThread,
        toolId
      } = ids();
      await discardDraft({
        variables: {
          input: {
            inboundCommentId,
            threadId: forThread,
            toolId
          }
        }
      });
      return {
        ok: true
      };
    } catch (error_1) {
      return {
        error: prettyErrorMessage(error_1),
        ok: false
      };
    }
  }
  return {
    claim,
    context,
    discard,
    lockedBy,
    on,
    refetch,
    save,
    send
  };
}